import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { WEB_COMPONENT_RECOVERY_MANAGER } from '../../../../../webcomponents.constants';
import { styles } from './view/styles';
import { FontSizes, FontWeights, LineHeights } from '../../../../../types/common/types';
import { translateFn } from '../../../../../utils/translator.helper';
import { InputTypes, InputWidthValues } from '../../../../../ui/gzInput/types';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { FormBuildConfig } from '../../../../../ui/gzForm/formGroup/types/form-build-config';
import { RecoveryManagerFormValue, RecoveryManagerRevealInfo } from './types';
import { ValidationResult } from '../../../../../ui/gzForm/formField/types/form-validator';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';

@customElement(WEB_COMPONENT_RECOVERY_MANAGER)
export class GzRecoveryManagerWebComponent extends LitElement {
  @property({ type: Boolean, attribute: false, reflect: false })
  hiddenPasswordField: Boolean = false;

  @property({ type: Object, attribute: true, reflect: true })
  revealInfo: RecoveryManagerRevealInfo | undefined = undefined;

  @query('gz-form-field#recovery-key-form-field')
  keyIdFormField: GzFormFieldWebComponent<GzInputWebComponent>;

  @query('gz-form-field#password-form-field')
  passwordFormField: GzFormFieldWebComponent<GzInputWebComponent>;

  private formGroup: FormGroup;

  static get styles() {
    return styles;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildFormGroup();
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.formGroup.validate();
  }

  public getFormValue(): RecoveryManagerFormValue {
    return this.formGroup.getValueRecursively(this.formGroup) as unknown as RecoveryManagerFormValue;
  }

  private validateKey(): ValidationResult {
    if (
      this.revealInfo?.volumeDetailsErrorCode !== undefined &&
      this.revealInfo.volumeDetailsErrorCode !== Constants.Encryption.RecoveryManager.Errors.INVALID_VOLUME_ERROR
    ) {
      const errorMessage =
        this.revealInfo?.volumeDetailsErrorCode === Constants.Encryption.RecoveryManager.Errors.INVALID_COMPANY_ID_ERROR
          ? translateFn('You do not have permission to manage this endpoint.')
          : translateFn('The recovery key ID does not exist in GravityZone.');

      this.revealInfo.volumeDetailsErrorCode = undefined;
      return {
        valid: false,
        error: errorMessage,
      };
    }

    return {
      valid: true,
    };
  }

  private validatePassword(): ValidationResult {
    if (this.revealInfo?.recoveryKeyErrorCode === Constants.Encryption.RecoveryManager.Errors.INVALID_PASSWORD_ERROR) {
      this.revealInfo.recoveryKeyErrorCode = undefined;

      return {
        valid: false,
        error: translateFn('Invalid GravityZone password'),
      };
    }

    return {
      valid: true,
    };
  }

  private buildFormGroup() {
    this.keyIdFormField.Validators = [
      RequiredValidator('This field is required.'),
      CustomizableValidator(this.validateKey.bind(this)),
    ];
    this.passwordFormField.Validators = [
      RequiredValidator('This field is required.'),
      CustomizableValidator(this.validatePassword.bind(this)),
    ];

    const formGroupConfig: FormBuildConfig = {
      keyId: this.keyIdFormField,
    };

    if (!this.hiddenPasswordField) {
      formGroupConfig['password'] = this.passwordFormField;
    }

    this.formGroup = FormGroup.build(formGroupConfig);

    this.formGroup.valid$.subscribe(value => {
      this.dispatchSetConfirmButtonEnabled(value);
    });
  }

  private renderHeaderText(): TemplateResult {
    return html`
      <gz-title
        fontSize="${FontSizes.PARAGRAPH_11}"
        fontWeight="${FontWeights.SEMIBOLD}"
        lineHeight="${LineHeights.SMALL}"
        class="text-uppercase mb-2"
      >
        ${translateFn('Encrypted volume identifier')}
      </gz-title>
    `;
  }

  private renderInputsContainer() {
    return html`
      <div id="inputs-container">
        <gz-form-field id="recovery-key-form-field" label="${translateFn('Recovery key ID')}">
          <gz-input
            gz-form-input
            placeholder="${translateFn('Enter the recovery key ID')}"
            inputWidth="${InputWidthValues.XLARGE}"
          >
          </gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
        <gz-form-field
          class="${this.hiddenPasswordField ? 'hidden' : ''}"
          id="password-form-field"
          label="${translateFn('Password')}"
        >
          <gz-input
            gz-form-input
            placeholder="${translateFn('Enter your GravityZone password')}"
            type="${InputTypes.PASSWORD}"
            inputWidth="${InputWidthValues.XLARGE}"
          >
          </gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private renderRevealVolumeDetails(): TemplateResult {
    return !this.isInvalidVolumeState()
      ? html`
          <gz-form-field id="volume-name-form-field" label="${translateFn('Volume name')}" readonly>
            <gz-input
              gz-form-input
              value="${this.revealInfo?.volumeDetails?.volumeName}"
              inputWidth="${InputWidthValues.XLARGE}"
            >
            </gz-input>
          </gz-form-field>
          <gz-form-field id="volume-type-form-field" label="${translateFn('Volume type')}" readonly>
            <gz-input gz-form-input value="${this.getTranslatedVolumeType()}" inputWidth="${InputWidthValues.XLARGE}">
            </gz-input>
          </gz-form-field>
          <gz-form-field id="endpoint-name-form-field" label="${translateFn('Endpoint name')}" readonly>
            <gz-input
              gz-form-input
              value="${this.revealInfo?.volumeDetails?.endpointName}"
              inputWidth="${InputWidthValues.XLARGE}"
            >
            </gz-input>
          </gz-form-field>
        `
      : html`
          <div>
            <gz-dialog-notification-content
              iconName="ds-icon-notification_negative_XL"
              iconColor="var(--red-layout-300-primary)"
            >
              <div slot="message">
                ${translateFn(
                  'Volume information is not available. The volume is unusable or the endpoint is not connected to GravityZone.'
                )}
              </div>
            </gz-dialog-notification-content>
          </div>
        `;
  }

  private renderRevealContainer(): TemplateResult {
    if (this.revealInfo === undefined || this.revealInfo.recoveryKeyErrorCode !== undefined) {
      return html`${nothing}`;
    }

    if (this.revealInfo.volumeDetailsErrorCode !== undefined && !this.isInvalidVolumeState()) {
      return html`${nothing}`;
    }

    return html`
      <div id="reveal-info-container">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          lineHeight="${LineHeights.SMALL}"
          class="text-uppercase mb-2 mt-3"
        >
          ${translateFn('Encrypted volume information')}
        </gz-title>
        ${this.renderRevealVolumeDetails()}
        <gz-copy-to-clipboard
          class="${this.isInvalidVolumeState() ? 'mt-2' : ''}"
          tooltipText="${translateFn('Copy recovery key to clipboard.')}"
        >
          <gz-form-field id="recovery-key-form-field" label="${translateFn('Recovery key')}" readonly>
            <gz-input gz-form-input value="${this.revealInfo.recoveryKey}" inputWidth="${InputWidthValues.XLARGE}">
            </gz-input>
          </gz-form-field>
        </gz-copy-to-clipboard>
      </div>
    `;
  }

  protected render(): unknown {
    return html` ${this.renderHeaderText()} ${this.renderInputsContainer()} ${this.renderRevealContainer()} `;
  }

  private getTranslatedVolumeType() {
    const type = this.revealInfo?.volumeDetails?.volumeType
      ? Constants.Encryption.VolumeType.BOOT
      : Constants.Encryption.VolumeType.NON_BOOT;
    return Translator.translateEncryptionVolumeType(type);
  }

  private dispatchSetConfirmButtonEnabled(value: boolean) {
    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        detail: { value: !value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private isInvalidVolumeState(): boolean {
    return this.revealInfo?.volumeDetailsErrorCode === Constants.Encryption.RecoveryManager.Errors.INVALID_VOLUME_ERROR;
  }
}
